import { FeatureTypes } from "entity-app/constants";
import { useContext, useEffect, useMemo, useState } from "react";
import ProjectWorkflowTemplateDropdown from "pages/preconstruction-details/components/workflow-template-dropdown";
import { Alert, Button, Select, Spin, Tooltip } from "antd";
import { useParams } from "react-router";
import {
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  ProjectPermissionEnum
} from "constants/index";
import ErrorBoundary from "components/error-boundary";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { TEntityDateblock, TGoverningSource } from "./models";
import EntityDateblock from ".";

type TFeatureInstance = TEntityDateblock & {
  title: string;
  feature_type_id: FeatureTypes;
  implicit: boolean;
};

type TLink = {
  governing_link: boolean;
  link_type: number;
  offset_duration?: any;
  linked_offset_date?: any;
  reverse_dependency?: boolean;
  source_feature_instance: TFeatureInstance;
  target_feature_instance: TFeatureInstance;
};

function DateblockSubmittalMaterialEntity(props: {
  featureInstanceData: any;
  afterChange?: () => void;
  disableDB?: boolean;
}) {
  const { projectId } = useParams() as any;
  const {
    tokenContents,
    columnHeaders: { submittalHeaderMap, materialHeaderMap }
  } = useContext(ProjectContext);
  const { featureInstanceData, afterChange, disableDB } = props;
  const sequenceId = featureInstanceData?.auto_inc_value;

  const [tempDisable, setTempDisable] = useState(false);
  const [materialEntity, setMaterialEntity] = useState<TEntityDateblock>();
  const [materialGoverningSource, setMaterialGoverningSource] =
    useState<TGoverningSource>();
  const [submittalGoverningSource, setSubmittalGoverningSource] =
    useState<TGoverningSource>();
  const [submittalEntity, setSubmittalEntity] = useState<TEntityDateblock>();

  const isSubmittal = featureInstanceData?.feature?.feature === "SUBMITTALS";
  const [submittalLinks, setSubmittalLinks] = useState<Array<TLink>>();
  const [materialLinks, setMaterialLinks] = useState<Array<TLink>>();
  const [
    isMaterialDependedEntityInProgress,
    setMaterialDependedEntityInProgress
  ] = useState(false);

  const isVoidEntity =
    featureInstanceData?.feature_workflow_instance?.workflow_status ===
    EWorkflowStatusDataBlock.VOID;
  const isDeletedEntity = featureInstanceData?.deleted_from_source;

  const cannotChangeDurationSubmittalDB =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditDateBlockOffsetSubmittal,
      tokenContents?.role!
    );

  const cannotChangeDurationMaterialDB =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditMaterialDBOffset,
      tokenContents?.role!
    );

  const cannotChangePlannedDate =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.changeMaterialPlannedDate,
      tokenContents?.role!
    );

  const cannotChangeActualDate =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.changeMaterialActualDate,
      tokenContents?.role!
    );

  const cannotChangeProjectedDate =
    disableDB ||
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditDateblockProjectedDate,
      tokenContents?.role!
    );

  const canSubmittalOffsetBeEdited = useMemo(() => {
    if (!submittalEntity) return false;
    const featureSubmittal: any =
      submittalEntity?.feature_submittals?.length &&
      submittalEntity?.feature_submittals[0];

    return (
      !submittalEntity?.deleted_from_source &&
      featureSubmittal?.workflow_status &&
      featureSubmittal?.workflow_status !== ESubmittalStatus.VOID
    );
  }, [submittalEntity]);

  const arePredecessorsComplete = useMemo(() => {
    return featureInstanceData?.feature_workflow_instance
      ?.are_predecessors_complete;
  }, [
    featureInstanceData?.feature_workflow_instance?.are_predecessors_complete
  ]);

  useEffect(() => {
    if (submittalEntity) {
      const governingTask = submittalEntity?.links_as_source?.find(
        (x) => x.governing_link
      );
      if (governingTask) {
        setSubmittalGoverningSource({
          id: governingTask?.target_feature_instance?.feature_workflow_instance
            .feature_instance_id,
          name: governingTask?.target_feature_instance?.title,
          type:
            governingTask?.target_feature_instance?.feature_type_id ===
            FeatureTypes.MATERIALS
              ? "Linked Material"
              : "Linked Activity",
          isLinkedEndDate: governingTask?.link_type === 2
        });
      } else if (
        submittalEntity?.feature_workflow_instance?.manual_entry_date
      ) {
        setSubmittalGoverningSource({
          id: submittalEntity.feature_workflow_instance.feature_instance_id,
          name: "Manually Set Date",
          type: "Manually Set Date"
        });
      } else {
        setSubmittalGoverningSource(undefined);
      }
    } else {
      setSubmittalGoverningSource(undefined);
    }
  }, [submittalEntity]);

  useEffect(() => {
    if (materialEntity) {
      const isAnySubmittalNotClosed = (
        materialEntity?.links_as_target as Array<TLink>
      )?.some(
        (x) =>
          x?.source_feature_instance?.feature_type_id ===
            FeatureTypes.SUBMITTALS &&
          [
            EWorkflowStatusDataBlock.IN_PROGRESS.toString(),
            EWorkflowStatusDataBlock.WF_NOT_STARTED.toString()
          ].includes(
            x?.source_feature_instance?.feature_workflow_instance
              ?.workflow_status
          )
      );

      setMaterialDependedEntityInProgress(isAnySubmittalNotClosed);

      const governingTask = (
        materialEntity?.links_as_source as Array<TLink>
      )?.find((x) => x.governing_link);
      if (governingTask) {
        setMaterialGoverningSource({
          id: governingTask?.target_feature_instance?.feature_workflow_instance
            .feature_instance_id,
          name: governingTask?.target_feature_instance?.title,
          type:
            governingTask?.target_feature_instance?.feature_type_id ===
            FeatureTypes.MATERIALS
              ? "Linked Material"
              : "Linked Activity",
          isLinkedEndDate: governingTask?.link_type === 2,
          offsetDuration: governingTask?.offset_duration,
          linkedOffsetDate: governingTask?.linked_offset_date,
          reverseDependency: governingTask?.reverse_dependency
        });
      } else if (materialEntity?.feature_workflow_instance?.manual_entry_date) {
        setMaterialGoverningSource({
          id: materialEntity?.feature_workflow_instance?.feature_instance_id,
          name: "Manually Set Date",
          type: "Manually Set Date"
        });
      } else {
        setMaterialGoverningSource(undefined);
      }
    } else {
      setMaterialGoverningSource(undefined);
    }
  }, [materialEntity]);

  useEffect(() => {
    if (isSubmittal) {
      const submittalEntit = featureInstanceData;
      const linkedFeatures =
        featureInstanceData?.links_as_source as Array<TLink>;
      let materialLins = linkedFeatures?.filter(
        (x: any) =>
          x.target_feature_instance?.feature_type_id === FeatureTypes.MATERIALS
      );

      if (materialLins.length > 1) {
        materialLins = materialLins.filter(
          (x) => !x.target_feature_instance.implicit
        );
      }

      if (materialLins.length) {
        const governing = materialLins.find((x) => x.governing_link);
        const material = governing || materialLins[0];
        setMaterialEntity(material.target_feature_instance);
      }

      setSubmittalEntity(submittalEntit);
      setMaterialLinks(materialLins);
    } else {
      const materialEntit = featureInstanceData as TEntityDateblock;
      const linkedFeatures =
        featureInstanceData?.links_as_target as Array<TLink>;

      const submittalLins = linkedFeatures?.filter(
        (x: any) =>
          x.source_feature_instance?.feature_type_id === FeatureTypes.SUBMITTALS
      );

      const submittalEntit = submittalLins?.length
        ? submittalLins[0].source_feature_instance
        : undefined;

      setMaterialEntity(materialEntit);
      setSubmittalEntity(submittalEntit);
      setSubmittalLinks(submittalLins);
    }
  }, [featureInstanceData, isSubmittal]);

  const submittalsListDropdownDiv = submittalLinks &&
    submittalLinks.length > 0 && (
      <div
        className="flex space-x-2 items-center"
        data-testid="dsme-submittals-dropdown-container"
      >
        <div
          className="text-xs font-bold tracking-wider"
          data-testid="dsme-submittals-label"
        >
          Submittals:
        </div>
        <Select
          className="w-96"
          options={submittalLinks?.map((x) => ({
            label: (
              <Tooltip
                title={x?.source_feature_instance?.title}
                className="flex space-x-1 truncate"
                data-testid="dsme-submittal-tooltip"
              >
                <div data-testid="dsme-submittal-title">
                  {" "}
                  {x?.source_feature_instance?.title}
                </div>
              </Tooltip>
            ),
            value:
              x?.source_feature_instance?.feature_workflow_instance
                ?.feature_instance_id
          }))}
          value={
            submittalEntity?.feature_workflow_instance?.feature_instance_id
          }
          onChange={(feature_instance_id) => {
            const currentSubmittal = submittalLinks?.find(
              (x) =>
                x.source_feature_instance.feature_workflow_instance
                  .feature_instance_id === feature_instance_id
            );
            setSubmittalEntity(currentSubmittal?.source_feature_instance);
          }}
          data-testid="dsme-submittals-select"
        />
      </div>
    );

  const materialsListDropdownDiv = materialLinks &&
    materialLinks.length > 0 && (
      <div
        className="flex space-x-2 items-center"
        data-testid="dsme-materials-dropdown-container"
      >
        <div
          className="text-xs font-bold tracking-wider"
          data-testid="dsme-materials-label"
        >
          Material:
        </div>
        <Select
          className="w-96"
          options={materialLinks?.map((x) => {
            const materialName = x.target_feature_instance.implicit
              ? `Material for Submittal ${sequenceId}`
              : x.target_feature_instance.title;
            return {
              label: (
                <Tooltip
                  title={materialName}
                  className="flex space-x-1 truncate"
                  data-testid="dsme-material-tooltip"
                >
                  {x.governing_link && (
                    <div data-testid="dsme-governing-indicator">*</div>
                  )}
                  <div data-testid="dsme-material-title"> {materialName}</div>
                </Tooltip>
              ),
              value:
                x?.target_feature_instance?.feature_workflow_instance
                  ?.feature_instance_id
            };
          })}
          value={materialEntity?.feature_workflow_instance?.feature_instance_id}
          onChange={(feature_instance_id) => {
            const currentMaterial = materialLinks?.find(
              (x) =>
                x.target_feature_instance.feature_workflow_instance
                  .feature_instance_id === feature_instance_id
            );
            setMaterialEntity(currentMaterial?.target_feature_instance);
          }}
          data-testid="dsme-materials-select"
        />
      </div>
    );

  const materialTemplateChangeDropdown = (
    <div
      className="flex items-center space-x-1 font-medium"
      data-testid="dsme-template-dropdown-container"
    >
      <div
        className="text-xs font-bold tracking-wider"
        data-testid="dsme-template-label"
      >
        Material Template:
      </div>
      {materialEntity && (
        <ProjectWorkflowTemplateDropdown
          className="w-96"
          afterChange={afterChange}
          featureId={FeatureTypes.MATERIALS}
          featureWorkflowInstance={materialEntity.feature_workflow_instance}
          setApiCallLoading={setTempDisable}
          disable={tempDisable}
          data-testid="dsme-template-dropdown"
        />
      )}
    </div>
  );

  const connectorLine = (
    <div className="pt-4" data-testid="dsme-connector-line">
      <div className="h-[7.25rem] w-0" data-testid="dsme-connector-vertical" />

      <div className="relative w-0 h-0" data-testid="dsme-connector-junction">
        <div
          className="absolute w-24 -left-24 border-0 border-t border-solid shrink-0 grow"
          data-testid="dsme-connector-left"
        />
        <div
          className="absolute w-0 left-0 h-28 -top-10 border-0 border-r border-solid shrink-0 grow"
          data-testid="dsme-connector-up"
        />
        <div
          className="absolute w-20 border-0 border-t border-solid shrink-0 grow"
          data-testid="dsme-connector-right"
        />
      </div>
    </div>
  );

  const canEditLinkingPage =
    !disableDB &&
    !isPermissionNotGrantted(
      ProjectPermissionEnum.EditLinkingPage,
      tokenContents?.role!
    );

  const noLinkedSubmittalDBDiv = materialEntity && (
    <div
      className="no-linking-item-found"
      data-testid="dsme-no-submittals-container"
    >
      <div
        className="no-linking-item-found--title"
        data-testid="dsme-no-submittals-title"
      >
        No Submittals Linked
      </div>
      {canEditLinkingPage && (
        <Button
          type="link"
          disabled={!canEditLinkingPage}
          href={`/project/${projectId}/schedule/material-linking?&materialId=${materialEntity?.feature_workflow_instance?.feature_instance_id}&linkSubmittal=true`}
          data-testid="dsme-link-submittals-button"
        >
          Click here to link
        </Button>
      )}
    </div>
  );

  const noLinkedMaterialItemDiv = submittalEntity &&
    submittalEntity.feature_submittals?.length && (
      <div
        className="no-linking-item-found"
        data-testid="dsme-no-materials-container"
      >
        <div
          className="no-linking-item-found--title text-center"
          data-testid="dsme-no-materials-title"
        >
          {submittalEntity?.feature_submittals[0]?.material_tracking
            ? "No Materials Linked"
            : "Material tracking is disabled"}
        </div>
        {canEditLinkingPage && (
          <Button
            type="link"
            disabled={!canEditLinkingPage}
            href={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${submittalEntity.feature_workflow_instance.feature_instance_id}`}
            data-testid="dsme-link-materials-button"
          >
            {submittalEntity.feature_submittals[0].material_tracking
              ? "Click here to add"
              : "Click here to enable"}
          </Button>
        )}
      </div>
    );

  if (!submittalEntity && !materialEntity)
    return (
      <div
        className="flex h-full w-full min-w-[500px] justify-center items-center"
        data-testid="dsme-loading-container"
      >
        <Spin data-testid="dsme-loading-spinner" />
      </div>
    );

  const showvoidSubmittalBanner = () => {
    return (
      <div data-testid="dsme-void-banner-content">
        This is <strong>Voided Submittal.</strong> Unlink Materials and
        Activities linked to this Submittal.
      </div>
    );
  };

  const showDeletedSubmittalBanner = () => {
    return (
      <div data-testid="dsme-deleted-banner-content">
        This is a <strong>Deleted Submittal.</strong> Unlink Materials and
        Activties linked to this Submittal.
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full" data-testid="dsme-root">
      {isVoidEntity && (
        <Alert
          className="border-0 text-center bg-ciq-void-100 top-0 left-0 w-full"
          message={showvoidSubmittalBanner()}
          type="info"
          data-testid="dsme-void-alert"
        />
      )}

      {isDeletedEntity && (
        <Alert
          className="border-0 text-center bg-ciq-void-100 top-0 left-0 w-full"
          message={showDeletedSubmittalBanner()}
          type="info"
          data-testid="dsme-deleted-alert"
        />
      )}
      <div
        className="overflow-auto px-1 flex h-full items-start p-6"
        data-testid="dsme-main-content"
      >
        <div className="flex flex-col" data-testid="dsme-submittal-section">
          <div className="h-10 grow px-6" data-testid="dsme-submittal-header">
            {!isSubmittal && submittalsListDropdownDiv}
          </div>
          {submittalEntity ? (
            <ErrorBoundary>
              <EntityDateblock
                entity={submittalEntity}
                afterChange={afterChange}
                governingSource={submittalGoverningSource}
                permissions={
                  tempDisable
                    ? {}
                    : {
                        canEditActualDate: false,
                        canEditProjectedDate:
                          isSubmittal && !cannotChangeProjectedDate,
                        canEditDurations:
                          !cannotChangeDurationSubmittalDB &&
                          canSubmittalOffsetBeEdited,
                        canEditPlannedDate: !cannotChangePlannedDate,
                        isDependedEntityInProgress: false,
                        arePredecessorsComplete: true
                      }
                }
                setApiCallLoading={setTempDisable}
                entityHeaders={submittalHeaderMap}
                data-testid="dsme-submittal-dateblock"
              />
            </ErrorBoundary>
          ) : (
            noLinkedSubmittalDBDiv
          )}
        </div>
        {submittalEntity && materialEntity && connectorLine}

        <div className="flex flex-col" data-testid="dsme-material-section">
          <div
            className="h-10 flex items-center space-x-2 px-5"
            data-testid="dsme-material-header"
          >
            {isSubmittal
              ? materialsListDropdownDiv
              : materialTemplateChangeDropdown}
          </div>
          {materialEntity ? (
            <ErrorBoundary>
              <EntityDateblock
                entity={materialEntity}
                afterChange={afterChange}
                governingSource={materialGoverningSource}
                permissions={
                  tempDisable
                    ? {}
                    : {
                        canEditActualDate:
                          !isSubmittal && !cannotChangeActualDate,
                        canEditProjectedDate:
                          !isSubmittal && !cannotChangeProjectedDate,
                        canEditDurations: !cannotChangeDurationMaterialDB,
                        canEditPlannedDate: !cannotChangePlannedDate,
                        isDependedEntityInProgress:
                          isMaterialDependedEntityInProgress,
                        arePredecessorsComplete
                      }
                }
                setApiCallLoading={setTempDisable}
                entityHeaders={materialHeaderMap}
                data-testid="dsme-material-dateblock"
              />
            </ErrorBoundary>
          ) : (
            noLinkedMaterialItemDiv
          )}
        </div>
      </div>
    </div>
  );
}

export default DateblockSubmittalMaterialEntity;
